import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletAPI } from '@/services/api';
import { createOrder } from '@/services/paymentService';
import { showSuccess, showError } from '@/utils/toastHelper';

/**
 * Custom hook for handling payment submission
 * @param {object} cart - Cart object
 * @param {function} handleGetTotalPrice - Function to get total price
 * @param {function} handleCalculateDeliveryFee - Function to calculate delivery fee
 * @param {object} promoApplied - Applied promo object
 * @param {number} walletDiscount - Wallet discount amount
 * @param {number} walletBalance - Wallet balance
 * @param {function} setPaymentConfirmedObject - Function to set payment confirmed object
 * @returns {{isSubmitting: boolean, submitPayment: function}} Submission state and function
 */
export const usePaymentSubmission = (
	cart,
	handleGetTotalPrice,
	handleCalculateDeliveryFee,
	promoApplied,
	walletDiscount,
	walletBalance,
	setPaymentConfirmedObject
) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();

	const buildOrderData = () => {
		const totalPrice = handleGetTotalPrice();
		const realDeliveryFee = handleCalculateDeliveryFee();
		const deliveryFee = promoApplied?.discountType === 'free' ? 0 : realDeliveryFee;
		const baseTotal = totalPrice.totalWithPromo >= 0 && promoApplied ? totalPrice.totalWithPromo : totalPrice.total;
		const amountToPay = Math.max(0, baseTotal + deliveryFee - (walletDiscount || 0));

		// Real TTC (before promo/wallet) for display; amount to pay stored separately when discount applied
		// Include delivery fees in realTtc even for free orders to show correct total
		const realTtc = totalPrice.total + realDeliveryFee;
		const hasDiscount = promoApplied || (walletDiscount > 0);

		return {
			restaurant_id: cart.restaurant_id,
			delivery: {
				customer: {
					firstname: cart.delivery_firstname,
					lastname: cart.delivery_lastname,
					phone: cart.delivery_phone,
					message: cart?.delivery_message,
				},
				address: cart.delivery_address,
				fees: deliveryFee,
				date: cart?.delivery_date,
				time: cart?.delivery_time,
			},
			billing: {
				customer: {
					firstname: cart.billing_firstname,
					lastname: cart.billing_lastname,
					email: cart.billing_email,
					phone: cart.billing_phone,
					message: cart?.billing_message,
					company: cart?.billing_company,
				},
				address: cart.billing_address,
			},
			selected_menus: cart.menus.map((menu) => ({
				id: menu.id,
				name: menu.name,
				quantity: menu.menu_quantity,
				price: menu.price,
				vat_id: menu?.vat_id,
			})),
			categories: cart.categories
				.filter((category) => 
					cart.is_no_limit && cart.is_no_limit_type === 'category' 
						? category.id === cart.is_no_limit_selected_category.id 
						: true
				)
				.map((category) => ({
					category_id: category.id,
					name: category.name,
					total_quantity:
						category.selected_products.reduce((acc, product) => acc + product.selected_quantity, 0) +
						category.extra_products.reduce((acc, product) => acc + product.selected_quantity, 0),
					products: category.selected_products.map((product) => ({
						id: product.id,
						product_id: product.product_id,
						name: product.name,
						quantity: product.selected_quantity,
						price: product.price,
						vat_id: product.vat_id,
						weight: product.weight
					})),
					extra_products: category.extra_products.map((extraProduct) => ({
						id: extraProduct.id,
						product_id: extraProduct.product_id,
						name: extraProduct.name,
						quantity: extraProduct.selected_quantity,
						price: extraProduct.price,
						vat_id: extraProduct.vat_id,
						weight: extraProduct.weight
					})),
				})),
			promo_code: cart?.promo_code ?? null,
			wallet_discount_amount: walletDiscount > 0 ? walletDiscount : null,
			total_price: realTtc,
			...(hasDiscount && { total_price_with_promo: amountToPay }),
			status: promoApplied?.discountType === 'free' ? 'validated' : 'pending_payment',
			is_no_limit: cart.is_no_limit
		};
	};

	const calculateTotal = () => {
		if (promoApplied?.discountType === 'free') {
			return 0;
		}

		const totalPrice = handleGetTotalPrice();
		const baseTotal = totalPrice.totalWithPromo >= 0 && promoApplied ? totalPrice.totalWithPromo : totalPrice.total;
		const deliveryFee = handleCalculateDeliveryFee();
		return Math.max(0, baseTotal + deliveryFee - (walletDiscount || 0));
	};

	const handleWalletApplication = async (orderReference) => {
		if (walletDiscount <= 0 || walletBalance <= 0) return;

		try {
			await walletAPI.apply(cart.billing_email, walletDiscount, orderReference);
		} catch (error) {
			// Error applying wallet discount - silently fail to not interrupt payment flow
		}
	};

	const handleFreeOrderSuccess = () => {
		showSuccess('Commande gratuite validée avec succès !', {
			autoClose: 1500,
		});
		localStorage.removeItem('restaurant_id_ref');
		sessionStorage.removeItem('selectedRestaurant');
		navigate('/commande/confirmation', { replace: true });
	};

	const handlePaidOrderSuccess = (total) => {
		navigate(`/commande/payment-form?amount=${total}&customerEmail=${cart.billing_email}&deliveryFees=${handleCalculateDeliveryFee()}`);
	};

	const submitPayment = async (e) => {
		e.preventDefault();

		if (isSubmitting) return;
		if (!cart.canSubmitPayment) {
			showError('Veuillez corriger les erreurs du formulaire (téléphone au format français 0X XX XX XX XX, email, etc.).');
			return;
		}

		if (promoApplied && promoApplied.is_reorder_discount === true && walletDiscount > 0) {
			showError('Le code de réduction n\'est pas cumulable avec le wallet. Veuillez annuler le code promo ou réinitialiser le wallet.');
			return;
		}

		setIsSubmitting(true);

		try {
			await handleWalletApplication('pending');

			const orderData = buildOrderData();
			const response = await createOrder(orderData);

			setPaymentConfirmedObject({
				restaurantId: cart?.restaurant_id,
				orderId: response.orderId,
				orderReference: response.reference
			});

			await handleWalletApplication(response.reference);

			const total = calculateTotal();

			if (promoApplied?.discountType === 'free') {
				handleFreeOrderSuccess();
			} else {
				handlePaidOrderSuccess(total);
			}
		} catch (error) {
			if (error.response?.data?.errors) {
				error.response.data.errors.forEach((err) => {
					showError(err.msg, {
						autoClose: 1200,
					});
				});
			}
			setIsSubmitting(false);
		}
	};

	return { isSubmitting, submitPayment };
};

